import * as PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import NoteActions from 'actions/NoteActions';
import DataConnector from 'connect/DataConnector';
import NoteStore from 'notebook/bioentities/NoteStore';
import {PRINT_OPTION} from 'notebook/notes/NoteConstants';
import {GeneralNoteViewProps} from 'notebook/notes/tabs/NoteViewProps';
import {NoteView} from 'notebook/notes/tabs/NoteViews';
import {ItemEditorState} from 'notebook/store/VersionedItemSubstore';
import {BioentityNote, Note, NotePart} from 'notebook/util/NoteUtilConstants';
import * as logging from 'util/Logging';

type ConnectedProps = ItemEditorState<BioentityNote, NotePart, 'note', 'fileId'> | null;
type Props = GeneralNoteViewProps & ConnectedProps;

class GeneralNoteViewConnector extends DataConnector<GeneralNoteViewProps, ConnectedProps> {
  shouldSubscribeToRedux = false;
  stores = [NoteStore];

  enhanceProps(): ConnectedProps {
    if (NoteStore.inStore(this.props.fileId)) {
      return this.noteStore().getEditorState(this.props.fileId);
    }
    return null;
  }
}

class UnconnectedGeneralNoteView extends React.Component<Props> {
  // TODO(T1083): Stop listening to NoteStore and have the file itself listen to the store.

  static propTypes = {
    banner: PropTypes.string,
    fileId: PropTypes.string.isRequired,
    updateStatusBar: PropTypes.func,
    clearStatusBar: PropTypes.func,
    allowEdits: PropTypes.bool.isRequired,
    preview: PropTypes.shape({
      followLink: PropTypes.bool.isRequired,
      showOpenLink: PropTypes.bool.isRequired,
    }),
    lockedFromExternalChange: PropTypes.bool.isRequired,
  };

  componentDidUpdate(prevProps: Props): void {
    if (!NoteStore.inStore(this.props.fileId)) {
      logging.error('NoteView was re-rendered though note is not present in the NoteStore');
    }
    if (
      this.props.saving !== prevProps.saving ||
      this.props.openVersion !== prevProps.openVersion ||
      this.props.savedVersion !== prevProps.savedVersion ||
      this.props.saveSuccess !== prevProps.saveSuccess
    ) {
      if (this.props.saving || this.props.openVersion !== this.props.savedVersion) {
        const msg = this.props.saveSuccess ? 'Saving...' : 'Retrying save...';
        if (this.props.updateStatusBar) {
          this.props.updateStatusBar(msg);
        }
      } else if (this.props.clearStatusBar) {
        this.props.clearStatusBar();
      }
    }
  }

  restoreStateAndSelection(
    {note}: {note: Note},
    selectionData: {data?: {selection: {cid: string; start?: number; end?: number}}}
  ): void {
    NoteActions.replace(this.props.fileId, note, selectionData);
  }

  printHandler(param: {showTimestamps: boolean}): void {
    NoteActions.print(this.props.fileId, param);
  }

  registerObjects(cidIdMap: {[cid: string]: number}): void {
    NoteActions.registerObjects(this.props.fileId, cidIdMap);
  }

  getIdFromCid(cid: string): string | number | undefined {
    return NoteStore.getIdFromCid(this.props.fileId, cid);
  }

  updateNote(note: Note): void {
    NoteActions.update(this.props.fileId, {$set: note});
  }

  render(): JSX.Element {
    if (this.props.note == null) {
      return <div>Loading...</div>;
    }

    return (
      <NoteView
        {..._.defaults(
          {
            getIdFromCid: (cid: string) => this.getIdFromCid(cid),
            printHandler: (param: {showTimestamps: boolean}) => {
              this.printHandler(param);
            },
            note: this.props.note,
            registerObjects: (cidIdMap: {[cid: string]: number}) => {
              this.registerObjects(cidIdMap);
            },
            updateNote: (note: Note) => {
              this.updateNote(note);
            },
            isLatest: this.props.isLatest,
            locked: !this.props.isLatest || !this.props.allowEdits,
            lockedFromExternalChange: this.props.lockedFromExternalChange,
            restoreStateAndSelection: (state: {note: Note; title: string}, selectionData: object) => {
              this.restoreStateAndSelection(state, selectionData);
            },
            historyManager: this.props.historyManager,
            printOptions: this.props.minimalToolbar ? [] : [PRINT_OPTION.NOTE],
            focusedTableProvenanceUuid: this.props.note.focusedTableProvenanceUuid,
          },
          this.props
        )}
      />
    );
  }
}

const GeneralNoteView = GeneralNoteViewConnector.connect(UnconnectedGeneralNoteView);
export default GeneralNoteView;
